import { ScopedCssBaseline } from '@mui/material';
import type {
  DataGridProProps,
  GridApi,
  GridColDef,
  GridRenderCellParams,
  GridRowClassNameParams,
} from '@mui/x-data-grid-pro';
import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_REORDER_COL_DEF,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import {
  DEFAULT_TABLE_DENSITY,
  type ItemStubsResponseDTO,
  type TableDensity,
  type TableLayoutDTO,
} from '@tempo-io/roadmap-dto';
import { cn } from '@tempo-io/sui-class-utils';
import * as React from 'react';
import type { Item, ItemDependency, ItemV2 } from '@/common/api/types/items';
import { MuiThemeProvider } from '@/common/components/MuiThemeProvider';
import { useFlag } from '@/common/hooks/feature-flags/useFlag';
import { useItemsTableColumnPinning } from './column-pinning/useItemsTableColumnPinning';
import type { OnColumnOrderChange } from './columns/useOnColumnOrderChange';
import { useOnColumnOrderChange } from './columns/useOnColumnOrderChange';
import type { OnColumnWidthChange } from './columns/useOnColumnWidthChange';
import { useOnColumnWidthChange } from './columns/useOnColumnWidthChange';
import type { OnSortModelChange } from './columns/useSortModel';
import { useSortModel } from './columns/useSortModel';
import { useTableColumns } from './columns/useTableColumns';
import { DENSITY_HEADER_HEIGHT, DENSITY_ROW_HEIGHT } from './constants';
import type { CommitCellMutation } from './context/TableCellMutationContext';
import { TableCellMutationProvider } from './context/TableCellMutationContext';
import { VisibleActualCostsProvider } from './context/VisibleActualCostsContext';
import {
  type FetchDependenciesByIds,
  VisibleDependenciesProvider,
} from './context/VisibleDependenciesContext';
import { VisibleHealthProvider } from './context/VisibleHealthContext';
import { VisibleIntegrationsProvider } from './context/VisibleIntegrationsContext';
import { VisibleItemFinancialsProvider } from './context/VisibleItemFinancialsContext';
import type { FetchItemsByIds, SyncSource } from './context/VisibleItemsContext';
import { useVisibleItems, VisibleItemsProvider } from './context/VisibleItemsContext';
import { VisibleSchedulesProvider } from './context/VisibleSchedulesContext';
import { VisibleUsersProvider } from './context/VisibleUsersContext';
import { useDatasetPages } from './hooks/useDatasetPages';
import { useItemPriorityReorder } from './hooks/useItemPriorityReorder';
import { useItemReparent } from './hooks/useItemReparent';
import { useItemsTableCell } from './hooks/useItemsTableCell';
import { useParticipatesInTreeSetSettleTracking } from './hooks/useParticipatesInTreeSetSettleTracking';
import { SETTLE_RETAINED_CLASS, useSettleRowClassName } from './hooks/useSettleRowClassName';
import { useTreeData } from './hooks/useTreeData';
import { useTreeSetEditingGuard } from './hooks/useTreeSetEditingGuard';
import { useVisiblePageIndices } from './hooks/useVisiblePageIndices';
import { useRegisterTableJump } from './jump-to-item/useRegisterTableJump';
import { ReorderCellWithSortTooltip } from './row-reordering/ReorderCellWithSortTooltip';
import { useCustomRowReorder } from './row-reordering/useCustomRowReorder';
import { usePublishBulkExpansionState } from './rows-expansion/usePublishBulkExpansionState';
import { useTableExpansionStorage } from './rows-expansion/useTableExpansionStorage';
import { SelectAllVisibleHeaderCheckbox } from './SelectAllVisibleHeaderCheckbox';
import type { ItemsTableRow } from './types';

const renderReorderCellWithSortTooltip = (params: GridRenderCellParams<ItemsTableRow>) => (
  <ReorderCellWithSortTooltip {...params} />
);

const REORDER_COL_OVERRIDE: GridColDef<ItemsTableRow> = {
  ...GRID_REORDER_COL_DEF,
  renderCell: renderReorderCellWithSortTooltip,
};

const SELECT_ALL_VISIBLE_CHECKBOX_COL: GridColDef<ItemsTableRow> = {
  ...GRID_CHECKBOX_SELECTION_COL_DEF,
  renderHeader: () => <SelectAllVisibleHeaderCheckbox />,
};

export const useItemsTableApiRef = () => useGridApiRef() as React.RefObject<GridApi>;

export type ItemsTableProps = Omit<
  DataGridProProps<ItemsTableRow>,
  | 'columns'
  | 'rows'
  | 'groupingColDef'
  | 'apiRef'
  | 'onColumnWidthChange'
  | 'onColumnOrderChange'
  | 'onSortModelChange'
  | 'treeData'
  | 'getTreeDataPath'
  | 'pinnedColumns'
  | 'onPinnedColumnsChange'
  | 'rowReordering'
  | 'isRowReorderable'
  | 'isValidRowReorder'
  | 'setTreeDataPath'
  | 'onRowOrderChange'
> & {
  apiRef?: React.RefObject<GridApi>;
  onGridMounted?: () => void;
  onGridUnmounted?: () => void;
  layout: TableLayoutDTO;
  density?: TableDensity;
  instanceKey?: string;
  readOnly?: boolean;
  cellsReadOnly?: boolean;
  layoutReadOnly?: boolean;
  disableRowReordering?: boolean;
  onColumnWidthChange?: OnColumnWidthChange;
  onColumnOrderChange?: OnColumnOrderChange;
  onSortModelChange?: OnSortModelChange;
  dataset?: ItemStubsResponseDTO;
  items?: Item[];
  fetchItemsByIds?: FetchItemsByIds;
  itemsSyncSource?: SyncSource<number, Partial<ItemV2>>;
  commitCellMutation?: CommitCellMutation;
  customGroupingColDef?: DataGridProProps['groupingColDef'];
  customColumns?: ReturnType<typeof useTableColumns>['columns'];
  targetDatesEditable?: boolean;
  loopId?: string;
  selectAllVisibleRows?: boolean;
  onReorderRow?: NonNullable<DataGridProProps<ItemsTableRow>['onRowOrderChange']>;
  reorderSiblingsOnly?: boolean;
  reorderingPaused?: boolean;
  /**
   * Set to false when the caller already fetches health/schedule data itself
   * (e.g. for sorting or a badge count outside the grid) and its columns don't
   * read from useVisibleItemHealth/useVisibleProgress/useVisibleSchedule — avoids
   * a redundant fetch alongside the caller's own. Defaults to true.
   */
  fetchHealth?: boolean;
  fetchSchedules?: boolean;
  fetchDependencies?: boolean;
  /**
   * Overrides where the dependencies column sources its data. Supply this when
   * the rows are not plain item-store items — e.g. a scenario table, whose
   * dependencies live in the scenario rather than the portfolio.
   */
  fetchDependenciesByIds?: FetchDependenciesByIds;
  /**
   * Overrides which per-id cache the dependencies column observes for live
   * updates. Pair with `fetchDependenciesByIds` so mutations that patch the
   * caller's own cache (e.g. scenario items) reach the column without a refetch.
   */
  dependenciesSyncSource?: SyncSource<number, ItemDependency[]>;
  /**
   * Whether this table's rows are backed by the real reporting-store tree-set network — pending/
   * settled/retained marking and the reconciliation editing guard both key off this. Set to false
   * for data outside that network (e.g. a scenario table's drafts). Defaults to true.
   */
  tracksTreeSetSettlement?: boolean;
};

const TABLE_SX = {
  border: 'none',
  borderTop: '1px solid var(--DataGrid-rowBorderColor)',
  borderRadius: 0,
  '& .MuiDataGrid-cell--editing': {
    boxShadow: 'none !important',
    backgroundColor: 'transparent !important',
    padding: '0 !important',
    outline: 'none !important',
  },
  '& .MuiDataGrid-cell--editing.dialog-edit-cell': {
    padding: '0 8px !important',
  },
  '& .MuiDataGrid-cell--editing:focus, & .MuiDataGrid-cell--editing:focus-within': {
    outline: 'none !important',
  },
  '& .items-table-row-unmatched .MuiDataGrid-cell > *': {
    opacity: 0.6,
  },
  '& .MuiDataGrid-cell': {
    transition: 'background-color 300ms ease-out',
  },
  // Cells paint their own background, so this must target every cell, not the row — and not
  // `:first-of-type`, which is the narrow `__reorder__` drag handle.
  '& .items-table-row-settle-pending .MuiDataGrid-cell': {
    backgroundColor: 'var(--color-bg-accent-blue-subtle)',
  },
  // Reinforces the tint rather than carrying the signal alone.
  '& .items-table-row-settle-pending .MuiDataGrid-cell:first-of-type': {
    boxShadow: 'inset 4px 0 0 0 var(--color-bg-accent-blue)',
  },
  // Held briefly rather than unmarked on arrival — the row usually just moved, and this is what
  // the user follows to find it.
  '& .items-table-row-settle-settled .MuiDataGrid-cell': {
    backgroundColor: 'var(--color-bg-success-subtle)',
  },
  // `info`, not `warning`: falling out of view after your own edit is expected, not a problem —
  // and distinct from `unmatched`'s dimming and `pending`'s blue, which mean something else.
  '& .items-table-row-settle-retained, & .items-table-row-settle-retained .MuiDataGrid-cell': {
    backgroundColor: 'var(--color-bg-info-subtle)',
  },
  '& .items-table-row-reparent-target, & .items-table-row-reparent-target .MuiDataGrid-cell': {
    backgroundColor: 'var(--color-bg-accent-blue-subtle)',
    outline: '1px solid var(--color-bg-accent-blue)',
    outlineOffset: '-1px',
  },
  '& .items-table-row-deleted .MuiDataGrid-cell': {
    backgroundColor: 'var(--color-bg-error-subtler)',
  },
  '& .items-table-row-deleted:hover .MuiDataGrid-cell': {
    backgroundColor: 'var(--color-bg-error-subtler-hovered)',
  },
  '& .items-table-row-deleted .MuiDataGrid-cell--pinnedLeft, & .items-table-row-deleted .MuiDataGrid-cell--pinnedRight':
    {
      backgroundImage:
        'linear-gradient(var(--color-bg-error-subtler), var(--color-bg-error-subtler))',
      backgroundColor: 'var(--DataGrid-t-color-background-base)',
    },
  '& .items-table-row-deleted:hover .MuiDataGrid-cell--pinnedLeft, & .items-table-row-deleted:hover .MuiDataGrid-cell--pinnedRight':
    {
      backgroundImage:
        'linear-gradient(var(--color-bg-error-subtler-hovered), var(--color-bg-error-subtler-hovered))',
      backgroundColor: 'var(--DataGrid-t-color-background-base)',
    },
  '& .MuiDataGrid-row--dropTarget': {
    boxShadow: 'none',
    outline: 'none',
    borderTop: 'none',
    borderBottom: 'none',
  },
  '& .items-table-row-highlighted .MuiDataGrid-cell': {
    animation: 'row-highlight-fade 2s ease-out',
  },
  '@keyframes row-highlight-fade': {
    '0%': { boxShadow: 'inset 0 0 0 9999px var(--color-bg-accent-blue-subtle)' },
    '100%': { boxShadow: 'inset 0 0 0 9999px transparent' },
  },
  '& .MuiDataGrid-columnHeaders .MuiDataGrid-filler': {
    height: '0 !important',
    alignSelf: 'flex-end',
  },
  '& .MuiDataGrid-filler--pinnedLeft': {
    background: 'var(--DataGrid-pinnedBackground) !important',
    borderRight: 0,
  },
} as const;

// The compact header row is only 32px tall, so the default sort arrow overflows
// its bounds and overlaps the column borders. Shrink the icon to fit.
const COMPACT_SX = {
  '& .MuiDataGrid-columnHeaders .MuiDataGrid-sortIcon': {
    fontSize: '0.75rem',
  },
} as const;

/**
 * Inner grid component: mounted inside the visible-items provider so its row
 * merge step can subscribe directly to the provider's accumulated map.
 */
const ItemsTableGrid = ({
  apiRef,
  skeletonRows,
  getTreeDataPath,
  layout,
  columns,
  groupingColDef,
  loading,
  initialState,
  density,
  checkboxSelection,
  getRowClassName,
  getHighlightClass,
  settleRowClassName,
  readOnly,
  layoutReadOnly,
  rowReorderingEnabled,
  instanceKey,
  pinnedColumns,
  onPinnedColumnsChange,
  isRowReorderable,
  isValidRowReorder,
  setTreeDataPath,
  onRowOrderChange,
  onItemsTableColumnWidthChange,
  onItemsTableColumnOrderChange,
  onItemsTableSortModelChange,
  cellModesModel,
  onCellModesModelChange,
  onCellClick,
  onCellEditStop,
  isCellEditable,
  sx,
  ...props
}: Omit<DataGridProProps<ItemsTableRow>, 'rows' | 'columns'> & {
  apiRef: React.RefObject<GridApi>;
  skeletonRows: ItemsTableRow[];
  getTreeDataPath: (row: ItemsTableRow) => readonly string[];
  layout: TableLayoutDTO;
  columns: DataGridProProps<ItemsTableRow>['columns'];
  groupingColDef: DataGridProProps<ItemsTableRow>['groupingColDef'];
  density: TableDensity;
  getRowClassName?: (params: GridRowClassNameParams<ItemsTableRow>) => string;
  getHighlightClass: (id: string | number) => string;
  settleRowClassName: (params: { id: string | number }) => string;
  readOnly?: boolean;
  layoutReadOnly?: boolean;
  rowReorderingEnabled: boolean;
  instanceKey?: string;
  pinnedColumns: DataGridProProps<ItemsTableRow>['pinnedColumns'];
  onPinnedColumnsChange: DataGridProProps<ItemsTableRow>['onPinnedColumnsChange'];
  isRowReorderable: DataGridProProps<ItemsTableRow>['isRowReorderable'];
  isValidRowReorder: DataGridProProps<ItemsTableRow>['isValidRowReorder'];
  setTreeDataPath: DataGridProProps<ItemsTableRow>['setTreeDataPath'];
  onRowOrderChange: DataGridProProps<ItemsTableRow>['onRowOrderChange'];
  onItemsTableColumnWidthChange: DataGridProProps<ItemsTableRow>['onColumnWidthChange'];
  onItemsTableColumnOrderChange: DataGridProProps<ItemsTableRow>['onColumnOrderChange'];
  onItemsTableSortModelChange: DataGridProProps<ItemsTableRow>['onSortModelChange'];
  cellModesModel: DataGridProProps<ItemsTableRow>['cellModesModel'];
  onCellModesModelChange: DataGridProProps<ItemsTableRow>['onCellModesModelChange'];
  onCellClick: DataGridProProps<ItemsTableRow>['onCellClick'];
  onCellEditStop: DataGridProProps<ItemsTableRow>['onCellEditStop'];
  isCellEditable: DataGridProProps<ItemsTableRow>['isCellEditable'];
}) => {
  const { itemsById } = useVisibleItems();

  const rows = React.useMemo(() => {
    if (skeletonRows.length === 0) return [];
    if (itemsById.size === 0) return skeletonRows;
    // Strip id/parentId from the source: skeletonRows owns the tree structure
    // (built by useTreeData) and a stale parentId from the cache must not
    // overwrite it.
    return skeletonRows.map((row) => {
      const data = itemsById.get(row.id);
      if (!data) return { ...row, __reorder__: String(row.id) };
      const { id: _id, parentId: _parentId, ...fields } = data;
      const merged = { ...row, ...fields };
      return { ...merged, __reorder__: merged.name ?? String(merged.id) };
    });
  }, [itemsById, skeletonRows]);

  const memoizedSx = React.useMemo(
    () => ({ ...TABLE_SX, ...(density === 'compact' ? COMPACT_SX : {}), ...sx }),
    [sx, density],
  );

  const combinedGetRowClassName = React.useCallback(
    (params: GridRowClassNameParams<ItemsTableRow>) => {
      const settleClass = settleRowClassName(params);
      // Retained already implies unmatched — apply it instead of stacking with the plain dimming.
      const isRetained = settleClass === SETTLE_RETAINED_CLASS;
      return cn(
        !params.row.matched && !isRetained && 'items-table-row-unmatched',
        getHighlightClass(params.id),
        settleClass,
        getRowClassName?.(params),
      );
    },
    [getRowClassName, getHighlightClass, settleRowClassName],
  );

  return (
    <DataGridPro
      rows={rows}
      treeData
      getTreeDataPath={getTreeDataPath}
      apiRef={apiRef}
      columns={columns}
      onColumnWidthChange={onItemsTableColumnWidthChange}
      onColumnOrderChange={onItemsTableColumnOrderChange}
      onSortModelChange={onItemsTableSortModelChange}
      loading={loading || !layout.columns?.length}
      groupingColDef={groupingColDef}
      initialState={initialState}
      checkboxSelection={checkboxSelection}
      rowSelectionPropagation={{ parents: false, descendants: false }}
      getRowClassName={combinedGetRowClassName}
      disableRowSelectionOnClick
      showColumnVerticalBorder
      showCellVerticalBorder
      disableColumnSelector
      disableMultipleColumnsSorting
      disableColumnFilter
      disableColumnMenu={layoutReadOnly}
      disableColumnPinning={readOnly || !instanceKey}
      disableColumnReorder={layoutReadOnly}
      disableColumnResize={layoutReadOnly}
      disableColumnSorting={layoutReadOnly}
      hideFooter
      tabNavigation="all"
      sortingMode="server"
      rowHeight={DENSITY_ROW_HEIGHT[density]}
      columnHeaderHeight={DENSITY_HEADER_HEIGHT[density]}
      cellModesModel={cellModesModel}
      onCellModesModelChange={onCellModesModelChange}
      isCellEditable={isCellEditable}
      onCellClick={onCellClick}
      onCellEditStop={onCellEditStop}
      pinnedColumns={pinnedColumns}
      onPinnedColumnsChange={readOnly ? undefined : onPinnedColumnsChange}
      rowReordering={rowReorderingEnabled}
      isRowReorderable={isRowReorderable}
      isValidRowReorder={isValidRowReorder}
      setTreeDataPath={setTreeDataPath}
      onRowOrderChange={onRowOrderChange}
      sx={memoizedSx}
      {...props}
    />
  );
};

/**
 * Shared MUI DataGridPro wrapper for all item tables.
 *
 * When mounted under a `<JumpToItemProvider>`, this table participates
 * automatically in jump-to-item — `Close & Jump` from any item panel under
 * the same provider scrolls and highlights the matching row, expanding
 * collapsed ancestors as needed. No props required.
 */
export const ItemsTable = ({
  dataset,
  items,
  layout,
  density = DEFAULT_TABLE_DENSITY,
  loading,
  initialState,
  sx,
  apiRef: apiRefProp,
  onGridMounted,
  onGridUnmounted,
  instanceKey,
  readOnly,
  cellsReadOnly,
  layoutReadOnly,
  disableRowReordering,
  checkboxSelection = true,
  onColumnWidthChange,
  onColumnOrderChange,
  onSortModelChange,
  getRowClassName,
  fetchItemsByIds,
  itemsSyncSource,
  commitCellMutation,
  customGroupingColDef,
  customColumns,
  targetDatesEditable,
  loopId,
  selectAllVisibleRows,
  onReorderRow,
  reorderSiblingsOnly,
  reorderingPaused,
  fetchHealth = true,
  fetchSchedules = true,
  fetchDependencies = true,
  fetchDependenciesByIds,
  dependenciesSyncSource,
  tracksTreeSetSettlement = true,
  ...props
}: ItemsTableProps) => {
  const internalApiRef = useGridApiRef();
  const apiRef = apiRefProp ?? (internalApiRef as React.RefObject<GridApi>);
  const onGridMountedRef = React.useRef(onGridMounted);
  onGridMountedRef.current = onGridMounted;
  const onGridUnmountedRef = React.useRef(onGridUnmounted);
  onGridUnmountedRef.current = onGridUnmounted;
  React.useEffect(() => {
    onGridMountedRef.current?.();
    return () => onGridUnmountedRef.current?.();
  }, []);
  const { getHighlightClass } = useRegisterTableJump(apiRef);
  const { markExpanded } = useTableExpansionStorage({
    instanceKey,
    apiRef,
    enabled: Boolean(instanceKey),
  });
  usePublishBulkExpansionState(apiRef);
  const generatedInstanceKey = React.useId();
  const resolvedInstanceKey = instanceKey ?? generatedInstanceKey;
  const defaultOnReparent = useItemReparent(resolvedInstanceKey);
  const defaultOnRowOrderChange = useItemPriorityReorder(apiRef, resolvedInstanceKey);
  const onReparent = reorderSiblingsOnly ? undefined : defaultOnReparent;
  const onRowOrderChange = onReorderRow ?? defaultOnRowOrderChange;

  const defaultColumns = useTableColumns(layout.columns, Boolean(items), {
    targetDatesEditable,
    loopId,
  });
  const groupingColDef = customGroupingColDef ?? defaultColumns.groupingColDef;
  const baseColumns = customColumns ?? defaultColumns.columns;
  const { pinnedColumns, onPinnedColumnsChange } = useItemsTableColumnPinning({ instanceKey });
  const isCellsReadOnly = cellsReadOnly ?? readOnly;
  const isLayoutReadOnly = layoutReadOnly ?? readOnly;
  const rowReorderingEnabled = !isCellsReadOnly && !disableRowReordering;
  const columns = React.useMemo(() => {
    const withReorder = rowReorderingEnabled ? [REORDER_COL_OVERRIDE, ...baseColumns] : baseColumns;
    if (!selectAllVisibleRows || !checkboxSelection) return withReorder;
    return [SELECT_ALL_VISIBLE_CHECKBOX_COL, ...withReorder];
  }, [baseColumns, rowReorderingEnabled, selectAllVisibleRows, checkboxSelection]);
  const { isRowReorderable, isValidRowReorder, setTreeDataPath, dragLegend } = useCustomRowReorder(
    apiRef,
    rowReorderingEnabled,
    onRowOrderChange,
    onReparent,
    markExpanded,
    reorderSiblingsOnly,
    reorderingPaused,
  );
  const onItemsTableColumnWidthChange = useOnColumnWidthChange(layout.columns, onColumnWidthChange);
  const onItemsTableColumnOrderChange = useOnColumnOrderChange(
    columns,
    layout.columns,
    onColumnOrderChange,
  );
  const { initialSortModel, onItemsTableSortModelChange } = useSortModel(
    layout.sort,
    onSortModelChange,
  );

  const { skeletonRows, getTreeDataPath } = useTreeData(dataset);
  const { pages, pageIndexById, datasetSignature } = useDatasetPages(dataset);
  const { visiblePageIndices, visibleItemIds } = useVisiblePageIndices({ apiRef, pageIndexById });
  const hasIntegrationColumn = React.useMemo(
    () => layout.columns.some((col) => col.type === 'integration'),
    [layout.columns],
  );
  const financeModuleEnabled = useFlag('financeModuleEnabled');
  const participatesInSettleTracking = useParticipatesInTreeSetSettleTracking(
    readOnly,
    tracksTreeSetSettlement,
  );
  const settleRowClassName = useSettleRowClassName(skeletonRows, participatesInSettleTracking);
  const hasItemFinancialsColumn = React.useMemo(
    () =>
      layout.columns.some(
        (col) =>
          col.type === 'budget' ||
          col.type === 'capexPercentage' ||
          col.type === 'forecastRevenueAmount',
      ),
    [layout.columns],
  );
  const hasActualCostColumn = React.useMemo(
    () => layout.columns.some((col) => col.type === 'actualCost'),
    [layout.columns],
  );

  const {
    editingRowId,
    cellModesModel,
    onCellModesModelChange,
    onCellClick,
    onCellEditStop,
    isCellEditable,
  } = useItemsTableCell(isCellsReadOnly);

  useTreeSetEditingGuard(editingRowId, participatesInSettleTracking);

  const memoizedInitialState = React.useMemo(
    () => ({
      sorting: { sortModel: initialSortModel },
      ...initialState,
    }),
    [initialSortModel, initialState],
  );

  return (
    <MuiThemeProvider>
      {dragLegend}
      <ScopedCssBaseline className="flex h-full w-full">
        <TableCellMutationProvider commitCellMutation={commitCellMutation}>
          <VisibleUsersProvider instanceKey={resolvedInstanceKey}>
            <VisibleItemsProvider
              pages={pages}
              visiblePageIndices={visiblePageIndices}
              pageIndexById={pageIndexById}
              datasetSignature={datasetSignature}
              apiRef={apiRef}
              instanceKey={resolvedInstanceKey}
              enabled={!items}
              staticItems={items}
              fetchItemsByIds={fetchItemsByIds}
              itemsSyncSource={itemsSyncSource}
            >
              <VisibleSchedulesProvider
                pages={pages}
                visiblePageIndices={visiblePageIndices}
                pageIndexById={pageIndexById}
                datasetSignature={datasetSignature}
                apiRef={apiRef}
                instanceKey={resolvedInstanceKey}
                enabled={fetchSchedules}
              >
                <VisibleHealthProvider
                  pages={pages}
                  visiblePageIndices={visiblePageIndices}
                  pageIndexById={pageIndexById}
                  datasetSignature={datasetSignature}
                  apiRef={apiRef}
                  instanceKey={resolvedInstanceKey}
                  enabled={fetchHealth}
                >
                  <VisibleDependenciesProvider
                    pages={pages}
                    visiblePageIndices={visiblePageIndices}
                    pageIndexById={pageIndexById}
                    datasetSignature={datasetSignature}
                    apiRef={apiRef}
                    instanceKey={resolvedInstanceKey}
                    enabled={fetchDependencies}
                    fetchDependenciesByIds={fetchDependenciesByIds}
                    dependenciesSyncSource={dependenciesSyncSource}
                  >
                    <VisibleIntegrationsProvider
                      visibleItemIds={visibleItemIds}
                      enabled={hasIntegrationColumn}
                    >
                      <VisibleItemFinancialsProvider
                        pages={pages}
                        visiblePageIndices={visiblePageIndices}
                        pageIndexById={pageIndexById}
                        datasetSignature={datasetSignature}
                        apiRef={apiRef}
                        instanceKey={resolvedInstanceKey}
                        enabled={hasItemFinancialsColumn && financeModuleEnabled}
                      >
                        <VisibleActualCostsProvider
                          pages={pages}
                          visiblePageIndices={visiblePageIndices}
                          visibleItemIds={visibleItemIds}
                          pageIndexById={pageIndexById}
                          datasetSignature={datasetSignature}
                          apiRef={apiRef}
                          instanceKey={resolvedInstanceKey}
                          enabled={hasActualCostColumn && financeModuleEnabled}
                        >
                          <ItemsTableGrid
                            apiRef={apiRef}
                            skeletonRows={skeletonRows}
                            getTreeDataPath={getTreeDataPath}
                            layout={layout}
                            columns={columns}
                            groupingColDef={groupingColDef}
                            loading={loading}
                            initialState={memoizedInitialState}
                            density={density}
                            checkboxSelection={checkboxSelection}
                            getRowClassName={getRowClassName}
                            getHighlightClass={getHighlightClass}
                            settleRowClassName={settleRowClassName}
                            readOnly={readOnly}
                            layoutReadOnly={isLayoutReadOnly}
                            rowReorderingEnabled={rowReorderingEnabled}
                            instanceKey={instanceKey}
                            pinnedColumns={pinnedColumns}
                            onPinnedColumnsChange={onPinnedColumnsChange}
                            isRowReorderable={isRowReorderable}
                            isValidRowReorder={isValidRowReorder}
                            setTreeDataPath={setTreeDataPath}
                            onRowOrderChange={onRowOrderChange}
                            onItemsTableColumnWidthChange={onItemsTableColumnWidthChange}
                            onItemsTableColumnOrderChange={onItemsTableColumnOrderChange}
                            onItemsTableSortModelChange={onItemsTableSortModelChange}
                            cellModesModel={cellModesModel}
                            onCellModesModelChange={onCellModesModelChange}
                            onCellClick={onCellClick}
                            onCellEditStop={onCellEditStop}
                            isCellEditable={isCellEditable}
                            sx={sx}
                            {...props}
                          />
                        </VisibleActualCostsProvider>
                      </VisibleItemFinancialsProvider>
                    </VisibleIntegrationsProvider>
                  </VisibleDependenciesProvider>
                </VisibleHealthProvider>
              </VisibleSchedulesProvider>
            </VisibleItemsProvider>
          </VisibleUsersProvider>
        </TableCellMutationProvider>
      </ScopedCssBaseline>
    </MuiThemeProvider>
  );
};
